import type { ListPageColumn } from '@/components/templates/list-page'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { OwnedChip } from '@/components/OwnedChip'
import { displayDateTime } from '@/lib/dates'

export interface EmailSend {
  id: number
  association_name: string
  contact_id: number | null
  contact_name: string | null
  kind: string
  kind_label: string
  recipient_email: string | null
  recipient_display_name?: string | null
  recipient_kind?: 'organization' | 'person' | null
  subject?: string | null
  status: 'pending' | 'sent' | 'failed' | 'skipped' | 'delivered' | 'bounced' | 'complained'
  skip_reason: string | null
  skip_connection_name: string | null
  system_email_template_version_id: number | null
  template_version_label: string | null
  sent_at: string
  chapter_name: string | null
  sender_name: string | null
  owner?: import('@/components/OwnedChip').Owner | string | null
}

export function emailSendColumns(includeContact: boolean): ListPageColumn<EmailSend>[] {
  const columns: ListPageColumn<EmailSend>[] = [
    {
      id: 'sent-at',
      header: 'Recorded',
      className: 'whitespace-nowrap',
      cell: (send) => formatSentAt(send.sent_at),
    },
  ]
  if (includeContact) {
    columns.push({
      id: 'contact',
      header: 'Contact',
      className: 'whitespace-nowrap',
      cell: (send) => (
        <span className="font-medium">{send.contact_name ?? send.recipient_email}</span>
      ),
    })
  }
  columns.push(
    {
      id: 'recipient',
      header: 'Recipient',
      className: 'whitespace-nowrap',
      cell: (send) => send.recipient_email ?? 'No address',
    },
    {
      id: 'kind',
      header: 'Email',
      className: 'whitespace-nowrap',
      cell: (send) => (
        <span className="flex items-center gap-2">
          {send.kind_label}
          <OwnedChip owner={send.owner} />
        </span>
      ),
    },
    {
      id: 'chapter',
      header: 'Chapter',
      className: 'whitespace-nowrap',
      cell: (send) => send.chapter_name ?? '—',
    },
    {
      id: 'sender',
      header: 'Sent by',
      headerClassName: 'whitespace-nowrap',
      className: 'whitespace-nowrap',
      cell: (send) => send.sender_name ?? '—',
    },
    {
      id: 'template-version',
      header: 'Template',
      className: 'whitespace-nowrap',
      cell: (send) => send.template_version_label ?? '—',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (send) => (
        <div className="flex items-center gap-2">
          <Badge variant={statusVariants[send.status]}>{statusLabels[send.status]}</Badge>
          {send.skip_reason ? (
            <span className="text-body-xs text-muted-foreground">
              {send.skip_reason === 'connection_owned' && send.skip_connection_name
                ? `Not sent: from ${send.skip_connection_name}`
                : sentenceCase(send.skip_reason)}
            </span>
          ) : null}
        </div>
      ),
    },
  )
  return columns
}

const statusVariants: Record<EmailSend['status'], BadgeProps['variant']> = {
  pending: 'neutral',
  sent: 'success',
  failed: 'destructive',
  skipped: 'warning',
  delivered: 'success',
  bounced: 'destructive',
  complained: 'destructive',
}

export const statusLabels: Record<EmailSend['status'], string> = {
  pending: 'Pending',
  sent: 'Sent',
  failed: 'Failed',
  skipped: 'Skipped',
  delivered: 'Delivered',
  bounced: 'Bounced',
  complained: 'Spam complaint',
}

function sentenceCase(value: string) {
  if (value === 'demo_association') return 'Demo association'
  return value.charAt(0).toUpperCase() + value.slice(1)
}

const formatSentAt = displayDateTime
